/* ── Option ─────────────────────────────────────────────────────────────────── */
export interface Option {
  id: number;
  letter: string;
  option_text: string;
}

/* ── Answer Slot (for dropdown, yes-no-statements, sortable-list) ────────── */
export interface AnswerSlot {
  slot_id: number;
  slot_text: string;
  choices: string[];
  correct_answer: string;
}

/* ── Question ──────────────────────────────────────────────────────────────── */
export interface Question {
  id: number;
  source_id: string;
  topic: string | null;
  subtopic?: string | null;
  question_text: string;
  question_type: string;
  has_image: boolean;
  correct_answer: string;
  answer_source: string | null;
  explanation: string | null;
  ms_docs_url: string | null;
  community_votes: Record<string, number> | null;
  difficulty: string;
  options: Option[];
  images?: string[] | null;
  answer_slots?: AnswerSlot[] | null;
  render_as?: string | null;
  created_at?: string;
  updated_at?: string;
}

/* ── Paginated question list ───────────────────────────────────────────────── */
export interface QuestionList {
  total: number;
  offset: number;
  limit: number;
  items: Question[];
}

/* ── Topic stats ───────────────────────────────────────────────────────────── */
export interface Topic {
  topic: string;
  count: number;
}

/* ── Subtopic (skill within a domain) ──────────────────────────────────────── */
export interface Subtopic {
  name: string;
  topic: string;
  functional_group: string | null;
  count: number;
}

/* ── Global statistics ─────────────────────────────────────────────────────── */
export interface Stats {
  total_questions: number;
  enriched_count: number;
  enriched_percent: number;
  by_topic: Topic[];
  by_difficulty: Record<string, number>;
}

/* ── SessionSpec (unified session filter contract) ──────────────────────────── */

/**
 * The filterable question types, kept in lockstep with the backend
 * `questions.question_type` CHECK enum and the `question_type` query regex.
 * `'ordering'` is intentionally excluded — it is referenced in code but not in
 * the DB CHECK, so it would always yield zero matches.
 */
export const QUESTION_TYPES = ['single', 'multiple', 'yesno', 'hotspot', 'dragdrop'] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

/**
 * Canonical, declarative description of what a study/exam session selects.
 * Single source of truth: sent as `filter_spec` on session creation, persisted
 * to the `user_sessions.filter_spec` JSONB column, and re-read on resume to
 * drive playback. Empty `topics`/`difficulties`/`questionTypes` mean "all";
 * `count: null` means unbounded. `subtopics` is a reserved, inert slot (no
 * backing column yet) — keep it `[]`.
 */
export interface SessionSpec {
  v: 1;
  certCode: string;
  mode: 'study' | 'exam';
  topics: string[];
  subtopics: string[];
  difficulties: string[];
  questionTypes: string[];
  count: number | null;
  smart: { onlyWrong: boolean; onlyUnseen: boolean; weakTopics: boolean };
  sessionName: string;
  seed: string;
}

/* ── Session ───────────────────────────────────────────────────────────────── */
export interface Session {
  id: number;
  session_name: string;
  mode: string;
  started_at: string;
  total_answered: number;
  correct_count: number;
  topic_filter?: string | null;
  difficulty_filter?: string | null;
  seed?: string | null;
  cursor_page?: number;
  cursor_idx?: number;
  cursor_global?: number;
  total_questions?: number;
  page_size?: number;
  is_completed?: boolean;
  updated_at?: string;
}

export interface SessionSummary {
  id: number;
  session_name: string;
  mode: string;
  topic_filter: string | null;
  difficulty_filter: string | null;
  cursor_global: number;
  total_questions: number;
  total_answered: number;
  correct_count: number;
  time_spent_seconds: number;
  is_completed: boolean;
  completed_at: string | null;
  started_at: string;
  updated_at: string;
}

export interface SessionList {
  total: number;
  items: SessionSummary[];
}

export interface CursorUpdatePayload {
  cursor_page: number;
  cursor_idx: number;
  cursor_global: number;
  total_questions: number;
}

export interface AnsweredQuestionBrief {
  question_id: number;
  given_answer: string;
  is_correct: boolean;
}

export interface SessionResumeData {
  id: number;
  session_name: string;
  mode: string;
  topic_filter: string | null;
  difficulty_filter: string | null;
  seed: string | null;
  cursor_page: number;
  cursor_idx: number;
  cursor_global: number;
  total_questions: number;
  page_size: number;
  is_completed: boolean;
  started_at: string;
  updated_at: string;
  total_answered: number;
  correct_count: number;
  answered_questions: AnsweredQuestionBrief[];
  filter_spec?: SessionSpec | null;
  certification_code?: string | null;
}

/* ── Answer review (single answer within results) ──────────────────────────── */
export interface AnswerReview {
  question_id: number;
  question_text: string;
  correct_answer: string;
  given_answer: string;
  is_correct: boolean;
  explanation: string | null;
}

/* ── Session results ───────────────────────────────────────────────────────── */
export interface SessionResults {
  session_id: number;
  mode: string;
  total: number;
  correct: number;
  score_percent: number;
  answers: AnswerReview[];
}

/* ── API request params ────────────────────────────────────────────────────── */
export interface QuestionsParams {
  topic?: string;
  subtopic?: string;
  difficulty?: string;
  offset?: number;
  limit?: number;
  seed?: string;
  certification?: string;
  question_type?: string;
}

export interface CreateSessionPayload {
  session_name: string;
  mode: 'study' | 'exam';
  topic_filter?: string;
  difficulty_filter?: string;
  seed?: string;
  page_size?: number;
  total_questions?: number;
  certification?: string;
  filter_spec?: SessionSpec;
}

export interface SubmitAnswerPayload {
  question_id: number;
  given_answer: string;
  time_spent_seconds?: number;
  exam_state?: ExamStatePayload;
  time_remaining?: number;
}

export interface SubmitAnswerResponse {
  id: number;
  question_id: number;
  given_answer: string;
  is_correct: boolean;
  correct_answer: string | null;
  explanation: string | null;
  time_spent_seconds: number | null;
  answered_at: string;
}

/* ── Reveal (exam review mode) ─────────────────────────────────────────────── */
export interface RevealRequest {
  question_id: number;
  given_answer?: string;
}

export interface RevealResponse {
  question_id: number;
  correct_answer: string;
  explanation: string | null;
  ms_docs_url: string | null;
  is_correct: boolean | null;
}

/* ── Exam save/resume ──────────────────────────────────────────────────────── */
export interface ExamStatePayload {
  question_order: number[];
  current_index: number;
  answers: Record<string, string>;
}

export interface ExamStateUpdatePayload {
  exam_state?: ExamStatePayload;
  time_remaining?: number;
}

/* ── Provider ─────────────────────────────────────────────────────────────── */
export interface ProviderOut {
  id: number;
  code: string;
  name: string;
  color: string;
}

/* ── Certification ────────────────────────────────────────────────────────── */
export interface CertificationTopic {
  id: number;
  name: string;
  display_order: number;
  weight_percent: number | null;
}

export interface CertificationSummaryOut {
  id: number;
  code: string;
  name: string;
  description: string;
  provider: ProviderOut;
  is_active: boolean;
}

export interface CertificationDetail {
  id: number;
  code: string;
  name: string;
  description: string | null;
  provider: ProviderOut;
  is_active: boolean;
  exam_duration_minutes: number;
  exam_question_count: number;
  passing_score_percent: number;
  docs_base_url: string | null;
  topics: CertificationTopic[];
}

export interface ActiveExam {
  id: number;
  session_name: string;
  mode: string;
  topic_filter: string | null;
  difficulty_filter: string | null;
  seed: string | null;
  total_questions: number;
  is_completed: boolean;
  started_at: string;
  updated_at: string;
  total_answered: number;
  correct_count: number;
  exam_state: ExamStatePayload | null;
  time_remaining: number | null;
  answered_questions: AnsweredQuestionBrief[];
}
